// Where received bytes go. OPFS by default (disk, resumable, no memory pressure for big
// videos); an in-memory fallback for engines without OPFS. close() returns a File the UI can
// save or share — for OPFS it is backed by disk, not copied into memory.

const MAX_PENDING = 32 * 1024 * 1024;
const PREFIX = 'drop-';

export interface Sink {
  /** Bytes already on disk for this name (resume after a reload), 0 for a fresh file. */
  readonly existing: number;
  write(offset: number, chunk: Uint8Array): void | Promise<void>;
  /** Resolves once queued writes are below the high-water mark. */
  drained(): Promise<void>;
  close(name: string, type: string): Promise<File>;
}

export function opfsAvailable(): boolean {
  return typeof Worker !== 'undefined' && !!navigator.storage?.getDirectory;
}

/** Opens the sink for `key` (an OPFS file name; never a name chosen by a remote peer). */
export async function openSink(key: string, { keep = false } = {}): Promise<Sink> {
  return opfsAvailable() ? openOpfsSink(PREFIX + key, keep) : memorySink();
}

/** Deletes received files that do not belong to the current session (`keepPrefix`). */
export async function cleanupOpfs(keepPrefix: string): Promise<void> {
  if (!opfsAvailable()) return;
  try {
    const root = await navigator.storage.getDirectory();
    for await (const name of root.keys()) {
      if (name.startsWith(PREFIX) && !name.startsWith(PREFIX + keepPrefix)) await root.removeEntry(name);
    }
  } catch {
    // Directory iteration is missing in some older engines; cleanup is best effort.
  }
}

async function openOpfsSink(name: string, keep: boolean): Promise<Sink> {
  const worker = new Worker(new URL('./opfs-worker.ts', import.meta.url), { type: 'module' });
  let pending = 0;
  let waiters: (() => void)[] = [];
  let onClosed: ((size: number) => void) | null = null;
  let onOpened: ((size: number) => void) | null = null;
  let failure: Error | null = null;
  const release = () => {
    if (pending > MAX_PENDING / 2) return;
    for (const w of waiters.splice(0)) w();
  };
  worker.onmessage = ({ data }) => {
    if (data.op === 'opened') onOpened?.(data.size);
    if (data.op === 'written') {
      pending -= data.bytes;
      release();
    }
    if (data.op === 'closed') onClosed?.(data.size);
    if (data.op === 'error') {
      failure = new Error(data.error);
      release();
    }
  };
  const existing = await new Promise<number>((resolve) => {
    onOpened = resolve;
    worker.postMessage({ op: 'open', name, keep });
  });

  const drained = () =>
    failure ? Promise.reject(failure) : pending <= MAX_PENDING ? Promise.resolve() : new Promise<void>((r) => waiters.push(r));

  return {
    existing,
    drained,
    write(offset, chunk) {
      if (failure) throw failure;
      // Copy: the payload is a view into a frame buffer that must not be detached.
      const buf = chunk.slice().buffer;
      pending += buf.byteLength;
      worker.postMessage({ op: 'write', at: offset, buf }, [buf]);
      if (pending > MAX_PENDING) return drained();
    },
    async close(displayName, type) {
      await new Promise<number>((resolve) => {
        onClosed = resolve;
        worker.postMessage({ op: 'close' });
      });
      worker.terminate();
      if (failure) throw failure;
      const root = await navigator.storage.getDirectory();
      const stored = await (await root.getFileHandle(name)).getFile();
      // Re-wrapping keeps the disk-backed blob (no copy) and gives it its real name and type.
      return new File([stored], displayName, { type });
    },
  };
}

function memorySink(): Sink {
  const parts: Uint8Array[] = [];
  return {
    existing: 0,
    drained: () => Promise.resolve(),
    write(_offset, chunk) {
      parts.push(chunk.slice());
    },
    async close(displayName, type) {
      return new File(parts as BlobPart[], displayName, { type });
    },
  };
}
